/**
 * Release 2 C4 — usage rights in the asset Info tab. The record is replaced as a whole (`setAssetRights`), so the
 * form keeps a draft of every key and saves it in one request; the server's `status` drives the badge.
 */
import { useId, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import type { Asset } from '@/lib/api/types';
import { useSetAssetRights, type AssetRights } from '@/lib/api/queries';
import { Button } from '@/components/Button';
import { Input, Select, Textarea } from '@/components/Input';
import { Badge } from '@/components/Misc';
import { formatDateTime } from '@/lib/format';
import { toast } from '@/stores/ui';
import { isApiError } from '@/lib/api/errors';
import { CC_VARIANTS, CC_VARIANT_LABEL, LICENSES, LICENSE_LABEL, RELEASES, RELEASE_LABEL, RIGHTS_STATUS_LABEL, RIGHTS_STATUS_TONE, dateInputToExpiry, expiryToDateInput, isEmbargoed, rightsStatusOf, type CcVariant, type RightsLicense, type ReleaseStatus } from './status';

// release4/R (META-3): + valid_from, copyright_owner, copyright_year, licensee, cc_variant
export type RightsKey = 'license' | 'credit_line' | 'restrictions' | 'model_release' | 'property_release' | 'notes' | 'expires_at' | 'valid_from' | 'copyright_owner' | 'copyright_year' | 'licensee' | 'cc_variant';
export const RIGHTS_KEYS: readonly RightsKey[] = ['license', 'credit_line', 'restrictions', 'model_release', 'property_release', 'notes', 'expires_at', 'valid_from', 'copyright_owner', 'copyright_year', 'licensee', 'cc_variant'];

/** Request body for the draft: every key present, empty strings → null (= not recorded). release4/R: `cc_variant` is dropped unless the license is Creative Commons. */
export function rightsBody(d: AssetRights, only?: ReadonlySet<RightsKey>): AssetRights {
  const out: Record<string, unknown> = {};
  for (const k of RIGHTS_KEYS) {
    if (only && !only.has(k)) continue;
    const v = d[k];
    if (k === 'copyright_year') {
      const n = typeof v === 'string' ? (v.trim() === '' ? NaN : Number(v)) : typeof v === 'number' ? v : NaN;
      out[k] = Number.isInteger(n) ? n : null;
      continue;
    }
    out[k] = typeof v === 'string' ? (v.trim() === '' ? null : v.trim()) : (v ?? null);
  }
  if ('cc_variant' in out && d.license !== 'creative_commons') out.cc_variant = null;
  return out as AssetRights;
}

function sameRights(a: AssetRights | undefined, b: AssetRights): boolean {
  return RIGHTS_KEYS.every((k) => (a?.[k] ?? null) === (b[k] ?? null));
}

export function RightsStatusBadge({ status }: { status: AssetRights['status'] }) {
  const s = status ?? 'none';
  return (
    <span data-rights-status={s}>
      <Badge tone={RIGHTS_STATUS_TONE[s]} className="gap-1">{RIGHTS_STATUS_LABEL[s]}</Badge>
    </span>
  );
}

/** The rights inputs, shared with the bulk dialog (`touched` marks the keys a merge would send). */
export function RightsFields({ value, onChange, disabled, touched }: { value: AssetRights; onChange: (k: RightsKey, v: string | number | null) => void; disabled?: boolean; touched?: ReadonlySet<RightsKey> }) {
  const base = useId();
  const mark = (k: RightsKey) => (touched?.has(k) ? 'ring-1 ring-accent' : undefined);
  const row = (k: RightsKey, label: string, input: React.ReactNode) => (
    <div className="grid grid-cols-[minmax(0,40%)_1fr] items-center gap-2 text-xs">
      <label htmlFor={`${base}-${k}`} className="truncate text-fg-subtle">{label}</label>
      {input}
    </div>
  );
  return (
    <div className="space-y-1.5" data-rights-fields>
      {row(
        'license',
        'License',
        <Select id={`${base}-license`} disabled={disabled} value={value.license ?? ''} className={`h-7 text-xs ${mark('license') ?? ''}`} onChange={(e) => onChange('license', (e.target.value || null) as RightsLicense | null)}>
          <option value="">—</option>
          {LICENSES.map((l) => <option key={l} value={l}>{LICENSE_LABEL[l]}</option>)}
        </Select>,
      )}
      {/* release4/R: CC variant only for Creative Commons */}
      {value.license === 'creative_commons' && row(
        'cc_variant',
        'CC variant',
        <Select id={`${base}-cc_variant`} disabled={disabled} value={value.cc_variant ?? ''} className={`h-7 text-xs ${mark('cc_variant') ?? ''}`} onChange={(e) => onChange('cc_variant', (e.target.value || null) as CcVariant | null)} data-cc-variant>
          <option value="">—</option>
          {CC_VARIANTS.map((v) => <option key={v} value={v}>{CC_VARIANT_LABEL[v]}</option>)}
        </Select>,
      )}
      {row('valid_from', 'Valid from', <Input id={`${base}-valid_from`} type="date" disabled={disabled} value={expiryToDateInput(value.valid_from)} className={`h-7 text-xs ${mark('valid_from') ?? ''}`} onChange={(e) => onChange('valid_from', e.target.value ? dateInputToValidFrom(e.target.value) : null)} />)}
      {row('expires_at', 'Expires', <Input id={`${base}-expires_at`} type="date" disabled={disabled} value={expiryToDateInput(value.expires_at)} className={`h-7 text-xs ${mark('expires_at') ?? ''}`} onChange={(e) => onChange('expires_at', e.target.value ? dateInputToExpiry(e.target.value) : null)} />)}
      {row('copyright_owner', 'Copyright owner', <Input id={`${base}-copyright_owner`} disabled={disabled} value={value.copyright_owner ?? ''} maxLength={500} className={`h-7 text-xs ${mark('copyright_owner') ?? ''}`} onChange={(e) => onChange('copyright_owner', e.target.value)} />)}
      {row('copyright_year', 'Copyright year', <Input id={`${base}-copyright_year`} type="number" min={1000} max={9999} disabled={disabled} value={value.copyright_year ?? ''} className={`h-7 text-xs ${mark('copyright_year') ?? ''}`} onChange={(e) => onChange('copyright_year', e.target.value === '' ? null : Number(e.target.value))} />)}
      {row('licensee', 'Licensee', <Input id={`${base}-licensee`} disabled={disabled} value={value.licensee ?? ''} maxLength={500} className={`h-7 text-xs ${mark('licensee') ?? ''}`} onChange={(e) => onChange('licensee', e.target.value)} />)}
      {row('credit_line', 'Credit line', <Input id={`${base}-credit_line`} disabled={disabled} value={value.credit_line ?? ''} maxLength={500} className={`h-7 text-xs ${mark('credit_line') ?? ''}`} onChange={(e) => onChange('credit_line', e.target.value)} />)}
      {row('restrictions', 'Restrictions', <Input id={`${base}-restrictions`} disabled={disabled} value={value.restrictions ?? ''} maxLength={2000} className={`h-7 text-xs ${mark('restrictions') ?? ''}`} onChange={(e) => onChange('restrictions', e.target.value)} />)}
      {(['model_release', 'property_release'] as const).map((k) =>
        <div key={k} className="contents">
          {row(
            k,
            k === 'model_release' ? 'Model release' : 'Property release',
            <Select id={`${base}-${k}`} disabled={disabled} value={value[k] ?? ''} className={`h-7 text-xs ${mark(k) ?? ''}`} onChange={(e) => onChange(k, (e.target.value || null) as ReleaseStatus | null)}>
              <option value="">—</option>
              {RELEASES.map((r) => <option key={r} value={r}>{RELEASE_LABEL[r]}</option>)}
            </Select>,
          )}
        </div>,
      )}
      {row('notes', 'Notes', <Textarea id={`${base}-notes`} disabled={disabled} value={value.notes ?? ''} rows={2} maxLength={5000} className={`text-xs ${mark('notes') ?? ''}`} onChange={(e) => onChange('notes', e.target.value)} />)}
    </div>
  );
}

/** release4/R: `<input type="date">` → ISO instant at the start of that local day (an embargo lifts at midnight). */
export function dateInputToValidFrom(value: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0).toISOString();
}

/** Info-tab section. Not rendered when the response carries no rights (share viewers). */
export function RightsEditor({ asset, canEdit }: { asset: Asset; canEdit: boolean }) {
  if (!asset.rights) return null;
  // a new server record (save, WS refetch) resets the draft
  return <RightsForm key={JSON.stringify(rightsBody(asset.rights))} asset={asset} rights={asset.rights} canEdit={canEdit} />;
}

function RightsForm({ asset, rights, canEdit }: { asset: Asset; rights: AssetRights; canEdit: boolean }) {
  const [draft, setDraft] = useState<AssetRights>(() => rightsBody(rights));
  const save = useSetAssetRights();
  const body = rightsBody(draft);
  const dirty = !sameRights(rightsBody(rights), body);
  const status = dirty ? rightsStatusOf(body.expires_at) : (rights.status ?? rightsStatusOf(rights.expires_at));
  const recorded = RIGHTS_KEYS.some((k) => rights[k] != null);
  return (
    <section data-rights-editor>
      <h3 className="mb-1 flex items-center gap-1.5 text-xs font-semibold tracking-wide text-fg-subtle uppercase">
        <ShieldCheck size={12} /> Usage rights <RightsStatusBadge status={status} />
      </h3>
      {!canEdit ? (
        recorded ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            {rights.license && <><dt className="text-fg-subtle">License</dt><dd>{LICENSE_LABEL[rights.license]}{rights.license === 'creative_commons' && rights.cc_variant ? ` · ${CC_VARIANT_LABEL[rights.cc_variant]}` : ''}</dd></>}
            {rights.valid_from && <><dt className="text-fg-subtle">Valid from</dt><dd>{formatDateTime(rights.valid_from)}{isEmbargoed(rights.valid_from) ? ' (embargoed)' : ''}</dd></>}
            {rights.expires_at && <><dt className="text-fg-subtle">Expires</dt><dd>{formatDateTime(rights.expires_at)}</dd></>}
            {rights.copyright_owner && <><dt className="text-fg-subtle">Copyright</dt><dd className="break-words">{rights.copyright_year ? `© ${rights.copyright_year} ` : ''}{rights.copyright_owner}</dd></>}
            {rights.licensee && <><dt className="text-fg-subtle">Licensee</dt><dd className="break-words">{rights.licensee}</dd></>}
            {rights.credit_line && <><dt className="text-fg-subtle">Credit line</dt><dd className="break-words">{rights.credit_line}</dd></>}
            {rights.restrictions && <><dt className="text-fg-subtle">Restrictions</dt><dd className="break-words">{rights.restrictions}</dd></>}
            {rights.model_release && <><dt className="text-fg-subtle">Model release</dt><dd>{RELEASE_LABEL[rights.model_release]}</dd></>}
            {rights.property_release && <><dt className="text-fg-subtle">Property release</dt><dd>{RELEASE_LABEL[rights.property_release]}</dd></>}
            {rights.notes && <><dt className="text-fg-subtle">Notes</dt><dd className="whitespace-pre-wrap">{rights.notes}</dd></>}
          </dl>
        ) : (
          <p className="text-xs text-fg-muted">No rights recorded</p>
        )
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate({ assetId: asset.id, rights: body }, { onError: (err) => toast({ kind: 'error', title: isApiError(err) ? err.message : 'Could not save rights' }) });
          }}
        >
          <RightsFields value={draft} onChange={(k, v) => setDraft((d) => ({ ...d, [k]: v }))} />
          {dirty && (
            <div className="mt-2 flex justify-end gap-1">
              <Button size="sm" type="button" onClick={() => setDraft(rightsBody(rights))}>Reset</Button>
              <Button size="sm" variant="primary" type="submit" disabled={save.isPending}>Save rights</Button>
            </div>
          )}
        </form>
      )}
    </section>
  );
}
